import { VideoFrameSource, type SourceFrame } from './video-frame-source'

export type FrameStallReason = 'source-no-progress' | 'processing-no-progress'

export function frameStallTimeoutMs(captureIntervalMs: number, processingIntervalMs: number, minimum = 15_000): number {
  return Math.max(1_000, minimum, captureIntervalMs * 3 + 2_000, processingIntervalMs * 3 + 2_000)
}

export interface FrameSchedulerHealth {
  readonly running: boolean
  readonly processing: boolean
  readonly stalled: boolean
  readonly stallReason?: FrameStallReason | null
  readonly stallTimeoutMs?: number
  readonly startedAt: number | null
  readonly lastVideoFrameAt: number | null
  readonly lastSuccessfulAt: number | null
  readonly lastSourceFrameAt?: number | null
  readonly sourceFrameId?: number | null
}

export interface FrameSchedulerOptions {
  readonly stallTimeoutMs?: number
  readonly captureIntervalMs?: () => number
  readonly now?: () => number
  readonly onStalled?: (health: FrameSchedulerHealth) => void
  readonly requestFrameRefresh?: (signal: AbortSignal) => Promise<unknown>
}

type FrameProcessor = (frame: SourceFrame, signal: AbortSignal) => Promise<boolean>

/** Timing only. VideoFrameSource owns freshness for every consumer. */
export class FrameScheduler {
  private running = false
  private processing = false
  private watchdogTimerId: number | null = null
  private lastRunAt = -Infinity
  private startedAt: number | null = null
  private lastSuccessfulAt: number | null = null
  private lastAttemptedId = 0
  private processingTask: Promise<boolean> | null = null
  private processingController: AbortController | null = null
  private processor: FrameProcessor | null = null
  private reportedStall: FrameStallReason | null = null
  private unlisten: (() => void) | null = null
  private heartbeatWorker: Worker | null = null
  private refreshController: AbortController | null = null
  private lastRefreshAt = -Infinity
  private readonly visibilityHandler = () => this.updateHiddenHeartbeat()

  constructor(private readonly source: VideoFrameSource, private intervalMs = 250,
    private readonly options: FrameSchedulerOptions = {}) {}

  setInterval(intervalMs: number): void { this.intervalMs = Math.max(16, intervalMs) }

  start(processFrame: FrameProcessor): void {
    if (this.running) return
    this.running = true
    this.processor = processFrame
    this.startedAt = this.now()
    this.lastRunAt = -Infinity
    this.lastSuccessfulAt = null
    this.reportedStall = null
    this.unlisten = this.source.listen(() => this.pulse())
    this.scheduleWatchdog()
    document.addEventListener('visibilitychange', this.visibilityHandler)
    this.updateHiddenHeartbeat()
  }

  pulse(_force = false): boolean {
    if (!this.running) return false
    const now = this.now()
    const frame = this.source.read()
    this.refreshIdleSource(now)
    const processor = this.processor
    if (!processor || this.processing || now - this.lastRunAt < this.intervalMs
      || !frame || frame.id <= this.lastAttemptedId) {
      this.reportStallIfNeeded(now)
      return false
    }
    // Try a genuinely new frame even after the watchdog deadline.
    this.lastAttemptedId = frame.id
    this.lastRunAt = now
    this.processing = true
    const controller = new AbortController()
    this.processingController = controller
    const task = Promise.resolve().then(() => controller.signal.aborted ? false : processor(frame, controller.signal))
    this.processingTask = task
    void task.then(successful => {
      if (!successful || controller.signal.aborted) return
      this.lastSuccessfulAt = this.now()
      this.reportedStall = null
    }, () => undefined).finally(() => {
      if (this.processingTask !== task) return
      this.processingTask = null
      this.processingController = null
      this.processing = false
      if (!controller.signal.aborted) this.reportStallIfNeeded(this.now())
    })
    return true
  }

  health(at = this.now()): FrameSchedulerHealth {
    const source = this.source.diagnostic()
    const timeout = this.stallTimeoutMs()
    const expired = (reference: number | null) => this.running && reference !== null && at - reference >= timeout
    const stallReason = expired(source.lastSourceFrameAt ?? this.startedAt) ? 'source-no-progress'
      : expired(this.lastSuccessfulAt ?? this.startedAt) ? 'processing-no-progress' : null
    return { running: this.running, processing: this.processing,
      stalled: stallReason !== null, stallReason, stallTimeoutMs: timeout,
      startedAt: this.startedAt, lastSuccessfulAt: this.lastSuccessfulAt, ...source }
  }

  stop(): void {
    this.running = false
    this.processor = null
    this.processingController?.abort()
    this.refreshController?.abort()
    this.refreshController = null
    this.unlisten?.(); this.unlisten = null
    if (this.watchdogTimerId !== null) window.clearTimeout(this.watchdogTimerId)
    this.watchdogTimerId = null
    document.removeEventListener('visibilitychange', this.visibilityHandler)
    this.stopHiddenHeartbeat()
  }

  async waitForIdle(): Promise<void> { await this.processingTask }

  private refreshIdleSource(now: number): void {
    const refresh = this.options.requestFrameRefresh
    const interval = this.options.captureIntervalMs?.() ?? 1000
    const reference = this.source.diagnostic().lastSourceFrameAt ?? this.startedAt
    if (!refresh || this.refreshController || reference === null
      || now - reference < Math.max(1000, interval + 500)
      || now - this.lastRefreshAt < Math.max(1000, interval)) return
    const controller = new AbortController()
    this.refreshController = controller
    this.lastRefreshAt = now
    // A refresh request is not frame evidence; only the source can advance identity.
    void Promise.resolve().then(() => controller.signal.aborted ? undefined : refresh(controller.signal))
      .catch(() => undefined).finally(() => {
        if (this.refreshController === controller) this.refreshController = null
      })
  }

  private scheduleWatchdog(): void {
    if (!this.running || this.watchdogTimerId !== null) return
    this.watchdogTimerId = window.setTimeout(() => {
      this.watchdogTimerId = null
      this.pulse()
      this.scheduleWatchdog()
    }, Math.max(100, Math.min(500, this.intervalMs / 2)))
  }

  private reportStallIfNeeded(now: number): void {
    const health = this.health(now)
    if (!health.stallReason) { this.reportedStall = null; return }
    // A fresh frame can still be processing; that is not a capture failure.
    if (health.stallReason === 'processing-no-progress' && this.processing && now - this.lastRunAt < this.stallTimeoutMs()
      || this.reportedStall === health.stallReason) return
    this.reportedStall = health.stallReason
    this.options.onStalled?.(health)
  }

  private updateHiddenHeartbeat(): void {
    if (!this.running || document.visibilityState !== 'hidden') {
      this.stopHiddenHeartbeat()
      return
    }
    if (this.heartbeatWorker || typeof Worker === 'undefined' || typeof URL.createObjectURL !== 'function') return
    try {
      const url = URL.createObjectURL(new Blob([
        'const timer=setInterval(()=>postMessage(0),1000);onmessage=()=>{clearInterval(timer);close()}',
      ], { type: 'text/javascript' }))
      const worker = new Worker(url)
      URL.revokeObjectURL(url)
      worker.onmessage = () => {
        if (this.running && document.visibilityState === 'hidden') this.pulse()
      }
      this.heartbeatWorker = worker
    } catch {
      this.heartbeatWorker = null
    }
  }

  private stopHiddenHeartbeat(): void {
    const worker = this.heartbeatWorker
    this.heartbeatWorker = null
    if (!worker) return
    try {
      worker.postMessage('stop')
      worker.terminate()
    } catch {
      // Hidden-page scheduling is best effort.
    }
  }


  private stallTimeoutMs(): number {
    return frameStallTimeoutMs(this.options.captureIntervalMs?.() ?? 0, this.intervalMs, this.options.stallTimeoutMs)
  }

  private now(): number { return this.options.now?.() ?? Date.now() }
}
